import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, PoundSterling, BarChart, ArrowRight, ShieldCheck } from 'lucide-react';
import Header from '@/components/landing/Header';

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }} 
    whileInView={{ opacity: 1, y: 0 }} 
    transition={{ duration: 0.5, delay }} 
    viewport={{ once: true }} 
    className="bg-gray-800/50 border border-gray-700 p-6 rounded-2xl flex flex-col items-center text-center"
  >
    <div className="w-14 h-14 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-4">
      <Icon className="w-7 h-7 text-blue-400" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

const AgentLandingPage = () => {
  return (
    <>
      <Helmet>
        <title>Agent Mode - Your Song Request</title>
        <meta name="description" content="Onboard your artists, track live earnings and requests in real-time, and automate commission splits. Build passive revenue from every gig you book with Your Song Request." />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-200 font-['Inter']">
        <Header />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden px-4">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-gray-900 to-black"></div>
          
          <div className="relative z-10 max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block px-4 py-1.5 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-300 text-sm font-semibold mb-6 tracking-wide uppercase"
            >
              YSR For Booking Agents
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
            >
              <span className="block md:inline">Your Roster.</span>{' '}
              <span className="block md:inline">More Revenue.</span>{' '}
              <span className="block md:inline">Zero Admin.</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-10"
            >
              Onboard your artists, track their live earnings and requests in real-time, and automate your commission splits. It's the easiest way to build a passive stream from every gig you book.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row justify-center gap-4"
            >
              <Link to="/agent/signup">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg py-6 px-10 rounded-xl shadow-[0_0_20px_rgba(37,99,235,0.4)] w-full sm:w-auto">
                  Create Agent Account <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-black px-4 border-t border-gray-800">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white">How Agent Mode Works</h2>
              <p className="mt-4 text-gray-400 max-w-2xl mx-auto">Simple onboarding, transparent tracking, and automated splits.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <FeatureCard 
                icon={Users}
                title="Custom Invite Link"
                description="Get a unique URL to send to your roster. When they sign up, they are permanently linked to your Agent account."
                delay={0.1}
              />
              <FeatureCard 
                icon={BarChart}
                title="Artist Tracking"
                description="View live dashboards for all your artists. See how many requests they're getting and their total gross revenue."
                delay={0.2}
              />
              <FeatureCard 
                icon={ShieldCheck}
                title="Flexible Splits"
                description="YSR takes 20% platform fee on every transaction. After that, you choose: Agent Managed—you receive 80% of gross and pay your artists yourself—or Auto Split—60% to the artist, 20% to your agency, 20% to YSR (our platform share)."
                delay={0.3}
              />
              <FeatureCard 
                icon={PoundSterling}
                title="Streamlined Weekly Payouts"
                description="Add your bank details securely and request agency payouts on a weekly rhythm—straight to your account when you're ready."
                delay={0.4}
              />
            </div>
          </div>
        </section>

        {/* Pricing / Split Section */}
        <section className="py-24 bg-gray-900 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Transparent Revenue Splits</h2>
            <p className="text-gray-400 text-lg mb-12 max-w-2xl mx-auto">
              YSR takes a standard 20% platform fee from gross revenue to cover payment processing, hosting, and development. You choose how the remaining 80% is handled.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div className="bg-gray-800 rounded-2xl p-8 border border-blue-500/30 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/10 blur-3xl rounded-full"></div>
                <h3 className="text-2xl font-bold text-white mb-2">Agent Managed (80/20)</h3>
                <p className="text-gray-400 text-sm mb-6">Best if you already pay your artists their gig fees directly.</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex justify-between items-center text-white font-medium border-b border-gray-700 pb-2">
                    <span>YSR Platform Fee</span> <span className="text-gray-400">20%</span>
                  </li>
                  <li className="flex justify-between items-center text-white font-medium border-b border-gray-700 pb-2">
                    <span>You Receive</span> <span className="text-blue-400 text-xl font-bold">80%</span>
                  </li>
                  <li className="flex justify-between items-center text-gray-500 text-sm">
                    <span>Artist Receives</span> <span>0% (from YSR)</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500">You handle paying the artist their cut off-platform according to your own contracts.</p>
              </div>

              <div className="bg-gray-800 rounded-2xl p-8 border border-purple-500/30 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-600/10 blur-3xl rounded-full"></div>
                <h3 className="text-2xl font-bold text-white mb-2">Auto Split (60/20/20)</h3>
                <p className="text-gray-400 text-sm mb-6">Best for hands-off management. YSR handles the artist payouts.</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex justify-between items-center text-white font-medium border-b border-gray-700 pb-2">
                    <span>YSR Platform Fee</span> <span className="text-gray-400">20%</span>
                  </li>
                  <li className="flex justify-between items-center text-white font-medium border-b border-gray-700 pb-2">
                    <span>You Receive</span> <span className="text-purple-400 text-xl font-bold">20%</span>
                  </li>
                  <li className="flex justify-between items-center text-white font-medium border-b border-gray-700 pb-2">
                    <span>Artist Receives</span> <span className="text-green-400 text-xl font-bold">60%</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500">Artists can request their 60% directly through their own YSR dashboard.</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Footer CTA */}
        <section className="py-20 bg-black text-center border-t border-gray-800 px-4">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to empower your roster?</h2>
          <Link to="/agent/signup">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-6 px-10 rounded-xl">
              Get Started as an Agent
            </Button>
          </Link>
        </section>

      </div>
    </>
  );
};

export default AgentLandingPage;
